// Source-agnostic task row for the generic record views (List/Board reuse it).
//
// Pure-ish: it never touches global stores — it resolves names/projects through
// the ResolverContext and applies edits via ctx.patch. This keeps it usable for
// project tasks, the cross-project hub, and personal lists alike, and unit-testable.
//
// ClickUp-style layout: comfortable rows, a colored priority flag, a soft status
// pill with an inline dropdown, an avatar stack, and a hover left-accent bar.

import { Bot, CalendarPlus, Check, ChevronDown, ChevronRight, Circle, Diamond, Flag, ListChecks } from "lucide-preact";
import type { TaskPriority } from "@juuplan/shared";
import { TASK_PRIORITIES } from "@juuplan/shared";
import { TASK_PRIORITY_LABEL, TASK_PRIORITY_VAR, formatDateShort, initials, localTodayIso, softPillStyle } from "../lib/format";
import type { ResolverContext, TaskRecord } from "../lib/taskRecord";
import type { RecordColumnId } from "../lib/viewState";
import { RowActions } from "./RowActions";
import { isBotUser, userTitle } from "./BotBadge";

interface Props {
  record: TaskRecord;
  ctx: ResolverContext;
  /** Columns the user has hidden. */
  hiddenColumns?: RecordColumnId[];
  /** Show the project chip (hub mode). Suppressed when already grouped by project. */
  showProject?: boolean;
  readOnly?: boolean;
  selected?: boolean;
  onSelect?: (uid: string) => void;
  anySelected?: boolean;
  onOpen?: (record: TaskRecord) => void;
  /** Delete the record — when provided (and not readOnly), a trash button
   *  appears in the row's hover-action cluster on the right. */
  onDelete?: (record: TaskRecord) => void | Promise<void>;
  /** When provided, a leading expand chevron is rendered (steps expansion). */
  onToggleExpand?: (uid: string) => void;
  /** Whether this row's step panel is currently expanded. */
  expanded?: boolean;
}

export function RecordRow(props: Props): preact.JSX.Element {
  const { record, ctx, hiddenColumns = [], showProject = false, readOnly = false } = props;
  const hidden = (c: RecordColumnId): boolean => hiddenColumns.includes(c);
  const project = record.projectId !== null ? ctx.resolveProject(record.projectId) : undefined;
  const statusOptions = ctx.resolveStatuses?.(record) ?? [];
  const canEditStatus = !readOnly && statusOptions.length > 0;

  const toggleDone = (e: Event): void => {
    e.stopPropagation();
    if (readOnly) return;
    void ctx.patch(record, { done: !record.done });
  };

  const overdue = !record.done && !!record.dueDate && record.dueDate < localTodayIso();

  return (
    <div
      class="group relative flex items-center gap-2.5 px-3 py-2.5 border-b border-[color:var(--color-outline-variant)]/60 hover:bg-[color:var(--color-surface-container-low)] hover:shadow-[inset_3px_0_0_var(--color-primary)] transition-[background,box-shadow] duration-100 cursor-pointer min-h-[44px]"
      onClick={() => props.onOpen?.(record)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Only when the row itself is focused — Enter inside an inline editor
        // (date picker, select) must not additionally open the detail.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          props.onOpen?.(record);
        }
      }}
    >
      {/* leading expand chevron — reveals the inline step list. Reserves its
          slot for all expandable rows so titles stay aligned; the count badge
          lives on the title (n/m). Faint on hover when there are no steps
          yet (still expandable so a first step can be added). */}
      {props.onToggleExpand && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            props.onToggleExpand?.(record.uid);
          }}
          class={`shrink-0 inline-flex size-5 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-opacity ${
            record.checklistTotal > 0 || props.expanded
              ? "opacity-100"
              : readOnly
                ? "opacity-0 pointer-events-none"
                : "opacity-0 md:group-hover:opacity-100 focus:opacity-100"
          }`}
          aria-label={props.expanded ? "Schritte ausblenden" : "Schritte einblenden"}
          aria-expanded={props.expanded ?? false}
          title={props.expanded ? "Schritte ausblenden" : "Schritte einblenden"}
        >
          {props.expanded ? (
            <ChevronDown size={16} aria-hidden="true" />
          ) : (
            <ChevronRight size={16} aria-hidden="true" />
          )}
        </button>
      )}

      {/* select checkbox — appears on hover or when a selection is active */}
      {props.onSelect && !readOnly && (
        <input
          type="checkbox"
          checked={props.selected ?? false}
          onClick={(e) => e.stopPropagation()}
          onChange={() => props.onSelect?.(record.uid)}
          class={`size-4 accent-[color:var(--color-primary)] cursor-pointer shrink-0 ${
            props.selected || props.anySelected ? "" : "opacity-100 md:opacity-0 md:group-hover:opacity-100"
          }`}
          aria-label={`${record.title} auswählen`}
        />
      )}

      {/* done toggle / milestone marker */}
      {record.isMilestone ? (
        <Diamond size={16} class="shrink-0 text-[color:var(--color-prio-urgent)]" fill="currentColor" aria-hidden="true" />
      ) : (
        <button
          type="button"
          onClick={toggleDone}
          disabled={readOnly}
          class="shrink-0 inline-flex items-center justify-center size-5 rounded-full text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-status-done)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:cursor-default"
          aria-label={record.done ? "Als offen markieren" : "Als erledigt markieren"}
          aria-pressed={record.done}
        >
          {record.done ? (
            <Check size={16} class="text-[color:var(--color-status-done)]" />
          ) : (
            <Circle size={16} />
          )}
        </button>
      )}

      {/* priority flag — colored when set, ghost outline when empty; inline picker */}
      {!hidden("priority") && (
        <span
          class="group/prio relative shrink-0 inline-flex items-center justify-center size-5"
          title="Priorität"
        >
          <Flag
            size={16}
            fill={record.priority ? "currentColor" : "none"}
            class={
              record.priority
                ? ""
                : "opacity-100 md:opacity-40 md:group-hover:opacity-100 transition-opacity"
            }
            style={{
              color: record.priority
                ? TASK_PRIORITY_VAR[record.priority]
                : "var(--color-outline)",
            }}
            aria-hidden="true"
          />
          {!readOnly && (
            <select
              value={record.priority ?? ""}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                const v = (e.currentTarget as HTMLSelectElement).value;
                void ctx.patch(record, { priority: v === "" ? null : (v as TaskPriority) });
              }}
              class="peer absolute inset-0 opacity-0 cursor-pointer"
              aria-label={`Priorität von ${record.title}`}
            >
              <option value="">Keine</option>
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {TASK_PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          )}
          {/* Bedienbarkeits-Hinweis: der Select ist `opacity-0` und liegt über
              der Fahne — ohne diesen Chevron sah die Fahne wie eine reine
              Anzeige aus. `peer-focus` deckt die Tastaturbedienung mit ab. */}
          {!readOnly && (
            <ChevronDown
              size={14}
              class="pointer-events-none absolute -bottom-1 -right-1 opacity-0 transition-opacity md:group-hover/prio:opacity-70 peer-focus:opacity-100 text-[color:var(--color-on-surface-variant)]"
              aria-hidden="true"
            />
          )}
        </span>
      )}

      {/* title */}
      <span class="flex-1 min-w-0 truncate text-sm">
        {record.ticket && (
          <span
            class="mr-1.5 font-mono text-micro font-medium text-[color:var(--color-on-surface-variant)] tabular-nums"
            title="Ticketnummer"
          >
            {record.ticket}
          </span>
        )}
        <span class={record.done ? "line-through opacity-60" : "text-[color:var(--color-on-surface)]"}>
          {record.title}
        </span>
        {record.checklistTotal > 0 && (
          <span
            class={`ml-2 inline-flex items-center gap-1 align-middle text-micro tabular-nums ${
              record.checklistDone === record.checklistTotal
                ? "text-[color:var(--color-status-done)] font-medium"
                : "text-[color:var(--color-on-surface-variant)]"
            }`}
            title={`${record.checklistDone} von ${record.checklistTotal} Schritten erledigt`}
          >
            <ListChecks size={14} aria-hidden="true" />
            {record.checklistDone}/{record.checklistTotal}
          </span>
        )}
      </span>

      {/* project chip */}
      {showProject && project && (
        <span
          class="hidden sm:inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-label font-medium shrink-0 max-w-[120px] truncate"
          style={softPillStyle(project.color ?? "var(--color-primary)")}
          title={project.name}
        >
          {project.name}
        </span>
      )}

      {/* assignees — avatar stack */}
      {!hidden("assignees") && record.assigneeIds.length > 0 && (
        // Neutrale Fuellung wie UserAvatar (vorher Akzent-Blau: Blau ist laut
        // KINETIC-FLOW-SYSTEM.md Aktionen/aktivem Zustand vorbehalten) und
        // ohne Ueberlappung, damit der Nachbar den zweiten Buchstaben nicht verdeckt.
        <span class="hidden md:flex items-center gap-0.5 shrink-0" role="group" aria-label="Zuständige">
          {record.assigneeIds.slice(0, 3).map((id) => {
            // Bots bekommen Glyph statt Initialen und die tertiary-Füllung —
            // dieselbe Konvention wie in UserAvatar/BotBadge.
            const bot = isBotUser(id);
            return (
              <span
                key={id}
                class={`inline-flex items-center justify-center size-6 rounded-full text-label font-semibold ring-2 ring-[color:var(--color-surface-container-lowest)] ${
                  bot
                    ? "bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]"
                    : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface)]"
                }`}
                role="img"
                aria-label={userTitle(id, ctx.resolveUserName(id))}
                title={userTitle(id, ctx.resolveUserName(id))}
              >
                {bot ? (
                  <Bot size={14} aria-hidden="true" />
                ) : (
                  initials(ctx.resolveUserName(id))
                )}
              </span>
            );
          })}
          {record.assigneeIds.length > 3 && (
            <span
              class="inline-flex items-center justify-center size-6 rounded-full bg-[color:var(--color-surface-container)] text-[color:var(--color-on-surface-variant)] text-label font-semibold ring-2 ring-[color:var(--color-surface-container-lowest)]"
              role="img"
              aria-label={`${record.assigneeIds.length - 3} weitere`}
            >
              +{record.assigneeIds.length - 3}
            </span>
          )}
        </span>
      )}

      {/* due date — inline editable via a native date picker overlaid on the
          cell (mirrors the priority/status inline-edit pattern). When empty and
          editable, a ghost calendar icon invites setting a date; read-only rows
          keep the plain date text (or render nothing when unset). */}
      {/* Visible on mobile too — the date is core row info; only the empty-state
          ghost icon stays desktop-only to keep phone rows uncluttered. */}
      {!hidden("end") && (record.dueDate || !readOnly) && (
        <span
          class={`relative items-center shrink-0 ${record.dueDate ? "inline-flex" : "hidden sm:inline-flex"}`}
          title="Fälligkeit"
        >
          {record.dueDate ? (
            <span
              class={`text-micro tabular-nums ${
                overdue
                  ? "text-[color:var(--color-prio-urgent)] font-semibold"
                  : "text-[color:var(--color-on-surface-variant)]"
              }`}
            >
              {formatDateShort(record.dueDate)}
            </span>
          ) : (
            <CalendarPlus
              size={16}
              class="text-[color:var(--color-outline)] opacity-100 md:opacity-40 md:group-hover:opacity-100 transition-opacity"
              aria-hidden="true"
            />
          )}
          {!readOnly && (
            <input
              type="date"
              value={record.dueDate ?? ""}
              onClick={(e) => {
                e.stopPropagation();
                const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                try {
                  el.showPicker?.();
                } catch {
                  /* showPicker may throw without a user gesture — the native
                     input still opens on focus/click as a fallback. */
                }
              }}
              onChange={(e) => {
                e.stopPropagation();
                const v = (e.currentTarget as HTMLInputElement).value;
                void ctx.patch(record, { dueDate: v || null });
              }}
              class="absolute inset-0 opacity-0 cursor-pointer"
              aria-label={`Fälligkeit von ${record.title}`}
            />
          )}
        </span>
      )}

      {/* status — mobile: compact color dot (the full pill needs too much room) */}
      {!hidden("status") && (
        <span
          class="sm:hidden inline-block size-2.5 rounded-full shrink-0"
          style={{ background: record.status.color }}
          title={`Status: ${record.status.label}`}
          aria-label={`Status: ${record.status.label}`}
          role="img"
        />
      )}

      {/* status pill (+ inline dropdown when statuses are available) */}
      {!hidden("status") && (
        <span
          class="relative hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-micro font-semibold shrink-0"
          style={softPillStyle(record.status.color)}
        >
          <span
            class="inline-block size-1.5 rounded-full shrink-0"
            style={{ background: record.status.color }}
            aria-hidden="true"
          />
          {record.status.label}
          {canEditStatus && (
            <select
              value={record.status.id ?? ""}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                const v = (e.currentTarget as HTMLSelectElement).value;
                if (v !== "") void ctx.patch(record, { statusId: Number(v) });
              }}
              class="absolute inset-0 opacity-0 cursor-pointer"
              aria-label={`Status von ${record.title}`}
            >
              {statusOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </span>
      )}

      {/* Hover-Aktionen: Bearbeiten + Löschen (rechts, erscheinen bei Hover) */}
      {!readOnly && props.onDelete && (
        <RowActions
          title={record.title}
          onEdit={() => props.onOpen?.(record)}
          onDelete={() => props.onDelete?.(record)}
        />
      )}
    </div>
  );
}
